<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { PopoverContentEmits, PopoverContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const popoverContentVariants = cva(
  'rounded-lg outline-hidden font-display font-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  {
    variants: {
      variant: {
        light: 'bg-white text-primal border border-gray-200 shadow-lg',
        dark: 'bg-dark/90 text-white',
      },
    },
    defaultVariants: {
      variant: 'light',
    },
  },
)

export type PopoverVariant = NonNullable<VariantProps<typeof popoverContentVariants>['variant']>

export type PopoverContentWrapperProps = PopoverContentProps & {
  variant?: PopoverVariant
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { PopoverContent, PopoverPortal, useForwardPropsEmits } from 'reka-ui'
import { useFloatingZ } from '#ui-kit/lib/useFloatingZ'
import { cn } from '#ui-kit/lib/utils'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<PopoverContentWrapperProps>(), {
  sideOffset: 4,
})
const emits = defineEmits<PopoverContentEmits>()

const delegated = reactiveOmit(props, 'class', 'variant')
const forwarded = useForwardPropsEmits(delegated, emits)
const floatingZ = useFloatingZ()
</script>

<template>
  <PopoverPortal>
    <PopoverContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn(popoverContentVariants({ variant: props.variant }), props.class)"
      :style="{ zIndex: floatingZ }"
    >
      <slot />
    </PopoverContent>
  </PopoverPortal>
</template>
